import { apiClient } from './client';

export type PlacementType = 'local_pc' | 'codespaces' | 'vps_backup' | 'peer_pc';
export type OsType = 'windows' | 'wsl2' | 'ubuntu' | 'codespaces' | 'other';
export type CliType = 'claude_code' | 'gemini_cli' | 'cursor_agent';
// エージェント（旧 persona）= 独立した人格。性別は人格表現の一要素。
export type AgentGender = 'male' | 'female' | 'neutral' | 'unset';
// タイプ（区分）= マスタ管理(agent_types)のコード値（dev/company/private…）。役割(role)の母集合を決める軸（ADR0006）。
// マスタで自由に追加できるため固定 union ではなく string（既存コードの可読性のため別名は維持）。
export type AgentType = string;
// 責務（役割）= マスタ管理(agent_roles_master)のコード値。type で絞り込む。固定 enum ではなく string。
export type AgentRole = string;
// エージェントの所属チーム（N:N の各要素）。担当プロジェクトは持たない（派生）。
export interface AgentTeam {
  id: number;
  name: string;
  icon: string | null;
  type: string;
}
export interface Agent {
  id: number;
  name: string;
  nickname: string | null; // 愛称。あれば優先表示
  type: AgentType; // 区分（開発系/会社系）
  role: AgentRole; // 責務（type で絞り込み）
  gender: AgentGender;
  icon: string | null; // 絵文字など。任意
  personality: string | null; // 性格・口調などの人格プロンプト。任意
  // 所属チーム（N:N）。複数チームに所属できる。担当プロジェクトは「所属チーム→DIR→project」で派生。
  teams: AgentTeam[];
  createdAt: string;
  updatedAt: string;
}

// #178: 一覧の並び替え可能列（backend AGENT_SORT_FIELDS と一致）。
export type AgentSortField =
  | 'id'
  | 'name'
  | 'nickname'
  | 'type'
  | 'role'
  | 'gender'
  | 'createdAt'
  | 'updatedAt';
export type SortDir = 'asc' | 'desc';

// #178: 一覧 API のクエリパラメータ。検索/ソート/ページングは常にサーバ側。
// type/role は #176 マスタのコード値（enum を超えて拡張されうるので string）。
export interface ListAgentsParams {
  q?: string; // 名前/愛称の部分一致（あいまい検索）
  type?: string; // 区分コードで絞り込み（'' / undefined は未指定）
  role?: string; // 責務コードで絞り込み（'' / undefined は未指定）
  sortBy?: AgentSortField;
  sortDir?: SortDir;
  page?: number; // 1 始まり
  pageSize?: number; // 1ページ件数
}

// #178: ページング結果。items だけでなく total/page/pageSize を返す。
export interface PaginatedAgents {
  items: Agent[];
  total: number;
  page: number;
  pageSize: number;
}

// 汎用ページング結果（teams/team-templates/conductors 等の /search 用・additive）。
export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}
// 汎用一覧クエリ。検索/ソート/ページングは常にサーバ側。
export interface ListQueryParams<S extends string = string> {
  q?: string;
  sortBy?: S;
  sortDir?: SortDir;
  page?: number;
  pageSize?: number;
}
// sortOrder = 一覧の D&D で決めた表示順（既定）。チャットのチーム列もこの順になる。
export type TeamSortField = 'sortOrder' | 'id' | 'name' | 'createdAt';
export type TeamTemplateSortField = 'id' | 'name' | 'createdAt';
export type ConductorSortField = 'id' | 'name' | 'type' | 'createdAt';
export type MountPointSortField = 'id' | 'name' | 'path' | 'createdAt';
// projects/devices/mount-purposes の /search 用（backend *_SORT_FIELDS と一致）。
export type ProjectSortField = 'id' | 'name' | 'type' | 'createdAt';
export type DeviceSortField = 'id' | 'name' | 'placementType' | 'osType' | 'createdAt';
export type MountPurposeSortField = 'id' | 'label' | 'sort' | 'createdAt';

// マスタ的な項目の絞り込みを足した検索パラメータ（あいまい検索 q + ドロップダウン絞り込み）。
export interface ListProjectsParams extends ListQueryParams<ProjectSortField> {
  type?: string; // 区分での絞り込み（'' / undefined は未指定）
}
export interface ListDevicesParams extends ListQueryParams<DeviceSortField> {
  osType?: OsType; // 実行環境(OS)での絞り込み
  placementType?: PlacementType; // 配置種別での絞り込み
}
export interface ListMountPurposesParams extends ListQueryParams<MountPurposeSortField> {
  enabled?: boolean; // 有効/無効での絞り込み
}
export interface ListTeamsParams extends ListQueryParams<TeamSortField> {
  type?: string; // 区分での絞り込み
}
export interface ListTeamTemplatesParams extends ListQueryParams<TeamTemplateSortField> {
  type?: string; // 区分での絞り込み
}
export interface ListConductorsParams extends ListQueryParams<ConductorSortField> {
  type?: ConductorType; // 種別での絞り込み
  enabled?: boolean; // 有効/無効での絞り込み
}
export interface ListMountPointsParams extends ListQueryParams<MountPointSortField> {
  enabled?: boolean; // 有効/無効での絞り込み
}

// ListQueryParams をクエリ文字列へ。空値は付けない。
function listQs(params: ListQueryParams): string {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.sortBy) qs.set('sortBy', params.sortBy);
  if (params.sortDir) qs.set('sortDir', params.sortDir);
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  return qs.toString() ? `?${qs}` : '';
}

// チーム = エージェント（人格）のグルーピング。Team 1:N Agent。
export interface TeamMember {
  id: number;
  name: string;
  nickname: string | null;
  icon: string | null;
}
// チーム内の役割スロット（枠）。role ごとに1枠、agent を割り当てる（未割当可）。
export interface TeamSlot {
  id: number;
  role: AgentRole;
  sortOrder: number;
  isLeader: boolean; // リーダー(PM)枠。チャットで @ 無し時の既定の会話相手はこの枠の agent。
  agentId: number | null;
  agent?: TeamMember | null;
}
// ワークフロー工程（工程フロー図の1工程）。担当役割が「いつ何をするか」を表す。
// action/handoffTo/onQuestion は当面フリーテキスト（仕様確定前・大規模修正中のため）。
export interface WorkflowStep {
  id: number;
  sortOrder: number;
  name: string; // 工程名（例: 設計 / 実装 / レビュー）
  ownerRole: AgentRole; // 担当役割（'unset'=担当なし）
  action: string | null; // 実行アクション（例: MCPでタスク登録）
  handoffTo: string | null; // 完了後の渡し先（例: 実装者へ）
  onQuestion: string | null; // 質問発生時の動き（例: チャットに報告）
}
// 工程の保存入力（id を持たない。順序は配列の並び順）。
export interface WorkflowStepInput {
  name: string;
  ownerRole?: string;
  action?: string | null;
  handoffTo?: string | null;
  onQuestion?: string | null;
}
export interface Team {
  id: number;
  name: string;
  type: string; // 区分（マスタ agent_types のコード値）。役割枠はこの区分の役割に限る。
  icon: string | null;
  description: string | null;
  sourceTemplateId: number | null;
  sourceTemplate?: { id: number; name: string } | null;
  // 表示順（小さいほど上）。チーム一覧の D&D で保存し、チャットのチーム列も同じ順で並ぶ。
  sortOrder: number;
  agents?: TeamMember[]; // 所属メンバー（listTeams 時に含む）
  slots?: TeamSlot[]; // 役割スロット（テンプレからコピー or 手動追加）
  workflowSteps?: WorkflowStep[]; // ワークフロー工程（工程フロー図）
  createdAt?: string;
  updatedAt?: string;
}

// チームテンプレート = 役割構成の雛形。slots は役割の順序付きリスト。
export interface TeamTemplateSlot {
  id: number;
  role: AgentRole;
  sortOrder: number;
  isLeader: boolean; // リーダー(PM)枠。チーム作成/適用時に TeamSlot.isLeader へ引き継がれる。
}
export interface TeamTemplate {
  id: number;
  name: string;
  type: string; // 区分（マスタ agent_types のコード値）。役割スロットはこの区分の役割に限る。
  icon: string | null;
  description: string | null;
  slots?: TeamTemplateSlot[];
  workflowSteps?: WorkflowStep[]; // ワークフロー工程の雛形（工程フロー図）
  createdAt?: string;
  updatedAt?: string;
}

// コンダクター（連携先）= システムがやりとりする相手の住所録。
// ── マスタ管理（#176）: エージェント区分(type) / 責務(role) ──
export interface AgentTypeMaster {
  id: number;
  code: string;
  label: string;
  sort: number;
  enabled: boolean;
}
export interface AgentRoleMaster {
  id: number;
  typeId: number;
  code: string;
  label: string;
  sort: number;
  enabled: boolean;
}
export interface AgentTypeWithRoles extends AgentTypeMaster {
  roles: AgentRoleMaster[]; // 責務は区分に従属
}

// ── /コマンドマスタ（CommandMaster・AI-WF #91）: 諸元のみ ──
// kind=種別 / scope=3層スコープ / runsOn=実行の落とし先（連携の入口ヒント）。
// 打鍵時のリモート挙動(連携仕様)は別タスク。ここは諸元の CRUD ＋ チャット用の有効一覧のみ。
export type CommandKind = 'builtin' | 'skill' | 'custom' | 'intent' | 'exec';
export type CommandScope = 'global' | 'project' | 'team';
export type CommandRunsOn =
  | 'claude-session'
  | 'ai-workflow'
  | 'remote-exec'
  | 'intent';
export interface CommandMaster {
  id: number;
  kind: string;
  scope: string;
  projectId: number | null;
  teamId: number | null;
  name: string;
  label: string;
  description: string | null;
  runsOn: string;
  target: string | null;
  invokeBy: string;
  sort: number;
  enabled: boolean;
}
export interface CommandInput {
  kind: string;
  scope: string;
  projectId?: number | null;
  teamId?: number | null;
  name: string;
  label: string;
  description?: string | null;
  runsOn: string;
  target?: string | null;
  invokeBy?: string;
  sort?: number;
  enabled?: boolean;
}
export interface ListCommandsParams {
  scope?: string;
  kind?: string;
  projectId?: number;
  teamId?: number;
}

// task_board = 仕事を取ってくる相手（タスク掲示板）/ chat_reporter = 報告・相談する相手（チャット報告者）
export type ConductorType = 'task_board' | 'chat_reporter';
export interface Conductor {
  id: number;
  name: string;
  type: ConductorType;
  endpoint: string | null; // 宛先（URL / MCP名 / Slackチャンネル等）。任意
  note: string | null; // メモ。任意
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

// マウントポイント = 論理名↔物理パスの割当マスター（論理名↔DIRマスター）。
// name = マウントポイント（参照の起点・tenant内で一意）/ path = マウント先の実体DIR。
// 【大前提】物理パスを書いてよい唯一の場所。以後の全参照はこの name を起点に解決する。
export interface MountPoint {
  id: number;
  name: string;
  path: string;
  description: string | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

// 用途マスタ = device-mount の「用途」選択肢の小マスター。label = 選択肢の値そのもの。
export interface MountPurpose {
  id: number;
  label: string;
  sort: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Company {
  id: number;
  name: string;
}
export interface Person {
  id: number;
  name: string;
}
export interface Project {
  id: number;
  type: string; // 'dev'(開発系) | 'company'(会社系)。会社は親ではない（ADR0003）。
  companyId: number | null;
  name: string; // dev コード名（例: sales-stocars）。
  nickname: string | null; // 愛称（日本語の表示名）。あれば表示優先。
  personaName: string | null; // 旧: 表示名。今は people を使う。
  chatOrder?: number; // チャット列の表示順。小さいほど上（DnDで保存）。
  chatArchived?: boolean; // チャット列でアーカイブ済みか（区切り線の下の区画に出る）。
  people?: Person[]; // プロジェクトの中の人（会話相手）。1:N（今は1人運用）。
  company?: Company;
}

// チャットの「コントローラ担当者」= バックエンド本体(コントローラAI)への @ 宛先（ユーザー単位）。
// 名前は変更可。そのユーザーの全チャットに常在する @ 候補。toRef は 'controller'。
export interface ControllerPersona {
  id: number;
  userId: number;
  name: string;
  icon: string | null;
}
// テナント単位のシステム設定(マスタメニュー)。会社で1個・全員共通。
export interface TenantSettings {
  // チャットの応答待ちタイムアウト(分)。MAX 値。既定 30。
  responseTimeoutMinutes: number;
}
// DIR に割り当てた担当チーム（アサイン本体）。1 DIR = 高々1チーム。
export interface AssignedTeam {
  id: number;
  name: string;
  icon: string | null;
  // チーム一覧の D&D で決めた表示順。チャットのチーム列はこの順で並べる。
  sortOrder: number;
}
export interface DeviceProjectMapEntry {
  id: number;
  deviceId: number;
  // パス登録時点では未束ね（null）。プロジェクトタブで束ねると projectId が付く。
  projectId: number | null;
  // 担当チーム（アサイン）。未アサインは null。
  teamId: number | null;
  cliType: CliType;
  // 参照する MountPoint。物理パス/論理名は mountPoint 側に持つ。
  mountPointId: number;
  mountPoint: { id: number; name: string; path: string };
  // DIR の用途/役割（任意・自由ラベル）。例: 「開発」。未設定は null。
  // 「誰が担当か(teamId)」とは別軸。デバイスまたぎで役割が違うため DIR 側に持つ。
  purpose: string | null;
  cliOptions: Record<string, unknown>;
  isRepresentative: boolean;
  // 有効/無効。false=無効（パスは残るが Worker への配信・ルーティング対象から外れる）。
  enabled: boolean;
  // WebSocket hello メッセージ（各デバイスの Claude への初期指示）。null = 指示なし。
  helloMessage: string | null;
  project?: Project | null;
  team?: AssignedTeam | null;
  // 1DIR複数チーム: primary(team) + 追加割当を union・重複排除・表示順した一覧（BE listDevices が付与）。
  // 未対応の古い BE 応答では undefined。その場合は team(単数) にフォールバックする。
  teams?: AssignedTeam[];
}

// Claude マスタ配布ジョブの実況 (AI-WF #50)。backend admin.service と同形。
export type ClaudeAuthProbe =
  | 'ok'
  | 'invalid_token'
  | 'not_logged_in'
  | 'expired'
  | 'rate_limited'
  | 'api_error'
  | 'no_cli'
  | 'unknown';
export interface ClaudeDistDevice {
  deviceId: number;
  name: string;
  online: boolean;
  state: 'pending' | 'probing' | 'offline' | 'timeout' | 'done';
  cliInstalled?: boolean;
  cliVersion?: string;
  keyMatch?: boolean;
  auth?: ClaudeAuthProbe;
  authEmail?: string;
  mounts?: { dir: string; exists: boolean }[];
  overall: 'ok' | 'pending' | 'warn' | 'error';
}
export interface ClaudeDistJob {
  jobId: string;
  masterFp: string | null;
  createdAt: number;
  done: boolean;
  masterSuspect: boolean;
  devices: ClaudeDistDevice[];
}

export interface DeviceListItem {
  id: number;
  name: string;
  nickname: string | null; // 愛称。あれば優先表示
  // VS Code Tunnel 名（手入力）。実ホスト名(name)とは別物で機械的に導出できない。
  // null = トンネル未登録。URL は https://vscode.dev/tunnel/{tunnelHost}{path}
  tunnelHost: string | null;
  placementType: PlacementType;
  osType: OsType;
  heartbeatEnabled: boolean;
  lastHeartbeatAt: string | null;
  online: boolean;
  // Claude Code(CLI) 利用可否。worker JWT(トークン)とは別レイヤ＝「この端末で claude が使えるか」。
  // installed/version は worker の `claude --version`、auth は実 run の成否から Backend が推定。
  claudeCliInstalled: boolean | null;
  claudeCliVersion: string | null;
  claudeAuthStatus: 'ok' | 'unauthenticated' | 'unknown' | null;
  claudeAuthEmail: string | null;
  claudeAuthPlan: string | null;
  claudeCheckedAt: string | null;
  // AI-WF #50: マスタ配布/最新確認の実測プローブ結果(一覧の多次元ステータス用)。
  claudeTokenFp?: string | null;
  claudeAuthProbe?: ClaudeAuthProbe | null; // 実走 claude の認証分類
  claudeDirOk?: boolean | null;
  claudeProbedAt?: string | null;
  claudeKeyMatch?: boolean | null; // 端末の鍵がマスタと一致するか(backend が比較)
  deviceProjectMap: DeviceProjectMapEntry[];
  loginId: string;
  jwtRevoked: boolean;
}

export interface NewDeviceResponse {
  id: number;
  name: string;
  nickname: string | null;
  tunnelHost: string | null;
  placementType: PlacementType;
  osType: OsType;
  heartbeatEnabled: boolean;
  loginId: string;
  password: string;
}

export const adminApi = {
  listCompanies: () => apiClient.get<Company[]>('/api/admin/companies').then((r) => r.data),
  createCompany: (name: string) =>
    apiClient.post<Company>('/api/admin/companies', { name }).then((r) => r.data),

  listProjects: () => apiClient.get<Project[]>('/api/admin/projects').then((r) => r.data),
  // 一覧画面用：サーバ側 検索(q=あいまい)/区分(type=ドロップダウン)/ソート/ページング（listProjects は温存）。
  searchProjects: (params: ListProjectsParams = {}) => {
    const qs = new URLSearchParams(listQs(params).replace(/^\?/, ''));
    if (params.type) qs.set('type', params.type);
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiClient.get<Paginated<Project>>(`/api/admin/projects/search${suffix}`).then((r) => r.data);
  },
  createProject: (input: {
    name: string;
    nickname?: string;
    type: string;
    companyId?: number;
    personaName?: string;
  }) => apiClient.post<Project>('/api/admin/projects', input).then((r) => r.data),
  // 変更 / 削除（teams 準拠の操作体系）。削除は紐づくものを自動デタッチして本体を消す（backend 側）。
  updateProject: (
    id: number,
    input: {
      name?: string;
      nickname?: string | null;
      type?: string;
      companyId?: number | null;
      personaName?: string;
    },
  ) => apiClient.patch<Project>(`/api/admin/projects/${id}`, input).then((r) => r.data),
  deleteProject: (id: number) =>
    apiClient.delete<{ ok: true }>(`/api/admin/projects/${id}`).then((r) => r.data),
  // チャットのプロジェクト配置を一括保存。items の並び順が表示順、archived がアーカイブ状態。
  // 並べ替えとアーカイブを 1 リクエストで保存（テナント共通）。返り値は更新後のプロジェクト一覧。
  setChatArrangement: (items: { id: number; archived: boolean }[]) =>
    apiClient
      .put<Project[]>('/api/admin/projects/chat-arrangement', { items })
      .then((r) => r.data),
  // チャットの「コントローラ担当者」(@宛先・ユーザー単位)。無ければ既定名で作って返す。
  getControllerPersona: () =>
    apiClient.get<ControllerPersona>('/api/admin/controller-persona').then((r) => r.data),
  updateControllerPersona: (input: { name?: string; icon?: string | null }) =>
    apiClient
      .patch<ControllerPersona>('/api/admin/controller-persona', input)
      .then((r) => r.data),
  // テナント単位のシステム設定(マスタメニュー)。会社で1個・全員共通。
  getTenantSettings: () =>
    apiClient.get<TenantSettings>('/api/admin/tenant-settings').then((r) => r.data),
  updateTenantSettings: (input: { responseTimeoutMinutes?: number }) =>
    apiClient.patch<TenantSettings>('/api/admin/tenant-settings', input).then((r) => r.data),
  createPerson: (projectId: number, name: string) =>
    apiClient.post<Person>(`/api/admin/projects/${projectId}/persons`, { name }).then((r) => r.data),

  // エージェント（旧 persona）CRUD
  // #178: 検索/ソート/ページングは常にサーバ側。全件取得はしない。
  listAgents: (params: ListAgentsParams = {}) => {
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.type) qs.set('type', params.type);
    if (params.role) qs.set('role', params.role);
    if (params.sortBy) qs.set('sortBy', params.sortBy);
    if (params.sortDir) qs.set('sortDir', params.sortDir);
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiClient.get<PaginatedAgents>(`/api/admin/agents${suffix}`).then((r) => r.data);
  },
  // 割り当て / チーム編成など「全件から選ぶ」用途。ページングを内部で全周し全件を返す
  // （一覧ページの検索とは別。一覧ページは listAgents をサーバ側ページングで使う）。
  listAllAgents: async (): Promise<Agent[]> => {
    const pageSize = 100;
    const first = await adminApi.listAgents({ pageSize, page: 1, sortBy: 'id', sortDir: 'asc' });
    const items = [...first.items];
    const pages = Math.ceil(first.total / pageSize);
    for (let p = 2; p <= pages; p++) {
      const next = await adminApi.listAgents({ pageSize, page: p, sortBy: 'id', sortDir: 'asc' });
      items.push(...next.items);
    }
    return items;
  },
  createAgent: (input: {
    name: string;
    nickname?: string | null;
    type?: AgentType;
    role?: AgentRole;
    gender?: AgentGender;
    icon?: string | null;
    personality?: string | null;
  }) => apiClient.post<Agent>('/api/admin/agents', input).then((r) => r.data),
  updateAgent: (
    id: number,
    input: {
      name?: string;
      nickname?: string | null;
      type?: AgentType;
      role?: AgentRole;
      gender?: AgentGender;
      icon?: string | null;
      personality?: string | null;
    },
  ) => apiClient.patch<Agent>(`/api/admin/agents/${id}`, input).then((r) => r.data),
  deleteAgent: (id: number) =>
    apiClient.delete<{ ok: true }>(`/api/admin/agents/${id}`).then((r) => r.data),

  // チーム（エージェントのグルーピング）CRUD
  listTeams: () => apiClient.get<Team[]>('/api/admin/teams').then((r) => r.data),
  // 一覧画面用：サーバ側 検索/ソート/ページング（既存 listTeams は温存）。
  searchTeams: (params: ListTeamsParams = {}) => {
    const qs = new URLSearchParams(listQs(params).replace(/^\?/, ''));
    if (params.type) qs.set('type', params.type);
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiClient.get<Paginated<Team>>(`/api/admin/teams/search${suffix}`).then((r) => r.data);
  },
  // type=区分。templateId 指定でテンプレの役割構成をコピーして作成（テンプレの区分と一致必須）。
  // 表示順の一括保存（一覧のドラッグ＆ドロップ）。ids の並びがそのまま表示順になる。
  // ページングで一部だけ渡しても、その並べ替え対象が元々占めていた位置に入れ直される。
  setTeamOrder: (ids: number[]) =>
    apiClient.put<Team[]>('/api/admin/teams/order', { ids }).then((r) => r.data),
  createTeam: (input: {
    name: string;
    type?: string;
    icon?: string | null;
    description?: string | null;
    templateId?: number | null;
  }) => apiClient.post<Team>('/api/admin/teams', input).then((r) => r.data),
  updateTeam: (
    id: number,
    input: { name?: string; type?: string; icon?: string | null; description?: string | null },
  ) => apiClient.patch<Team>(`/api/admin/teams/${id}`, input).then((r) => r.data),
  deleteTeam: (id: number) =>
    apiClient.delete<{ ok: true }>(`/api/admin/teams/${id}`).then((r) => r.data),
  // 編成画面：既存チームにテンプレの役割構成を流し込む（既存枠を全置換）。
  // 1 リクエストで枠の置き換えと sourceTemplate の記録を行う（一覧のテンプレ列に反映される）。
  applyTeamTemplate: (teamId: number, templateId: number) =>
    apiClient
      .post<Team>(`/api/admin/teams/${teamId}/apply-template`, { templateId })
      .then((r) => r.data),
  // チーム役割スロット（枠）: 追加 / 更新（役割・並べ替え・割当） / 削除
  // ※チーム所属は枠への割当の結果として増減する（所属の直接設定APIは持たない）。
  addTeamSlot: (teamId: number, role: AgentRole) =>
    apiClient.post<Team>(`/api/admin/teams/${teamId}/slots`, { role }).then((r) => r.data),
  updateTeamSlot: (
    slotId: number,
    input: { role?: AgentRole; sortOrder?: number; agentId?: number | null; isLeader?: boolean },
  ) => apiClient.patch<Team>(`/api/admin/team-slots/${slotId}`, input).then((r) => r.data),
  deleteTeamSlot: (slotId: number) =>
    apiClient.delete<Team>(`/api/admin/team-slots/${slotId}`).then((r) => r.data),
  // チームのワークフロー工程（工程フロー図）を丸ごと置き換える（順序は配列の並び順）。
  // 役割枠の編成とは別操作。担当役割はチームの区分のマスタ役割に限る（backend で検証）。
  setTeamWorkflow: (teamId: number, steps: WorkflowStepInput[]) =>
    apiClient.put<Team>(`/api/admin/teams/${teamId}/workflow`, { steps }).then((r) => r.data),

  // チームテンプレート（役割構成の雛形）CRUD
  listTeamTemplates: () =>
    apiClient.get<TeamTemplate[]>('/api/admin/team-templates').then((r) => r.data),
  // 一覧画面用：サーバ側 検索/ソート/ページング（既存 listTeamTemplates は温存）。
  searchTeamTemplates: (params: ListTeamTemplatesParams = {}) => {
    const qs = new URLSearchParams(listQs(params).replace(/^\?/, ''));
    if (params.type) qs.set('type', params.type);
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiClient
      .get<Paginated<TeamTemplate>>(`/api/admin/team-templates/search${suffix}`)
      .then((r) => r.data);
  },
  createTeamTemplate: (input: {
    name: string;
    type?: string;
    icon?: string | null;
    description?: string | null;
    roles?: AgentRole[];
    leaderIndex?: number | null; // roles 上のリーダー(PM)枠の位置。省略時は先頭(0)。
    workflow?: WorkflowStepInput[];
  }) => apiClient.post<TeamTemplate>('/api/admin/team-templates', input).then((r) => r.data),
  updateTeamTemplate: (
    id: number,
    input: {
      name?: string;
      type?: string;
      icon?: string | null;
      description?: string | null;
      roles?: AgentRole[];
      leaderIndex?: number | null; // roles 指定時のリーダー枠の位置。省略時は先頭(0)。
      workflow?: WorkflowStepInput[];
    },
  ) => apiClient.patch<TeamTemplate>(`/api/admin/team-templates/${id}`, input).then((r) => r.data),
  deleteTeamTemplate: (id: number) =>
    apiClient.delete<{ ok: true }>(`/api/admin/team-templates/${id}`).then((r) => r.data),

  // コンダクター（連携先）CRUD
  listConductors: () =>
    apiClient.get<Conductor[]>('/api/admin/conductors').then((r) => r.data),
  // 一覧画面用：サーバ側 検索/ソート/ページング（既存 listConductors は温存）。
  searchConductors: (params: ListConductorsParams = {}) => {
    const qs = new URLSearchParams(listQs(params).replace(/^\?/, ''));
    if (params.type) qs.set('type', params.type);
    if (params.enabled !== undefined) qs.set('enabled', String(params.enabled));
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiClient
      .get<Paginated<Conductor>>(`/api/admin/conductors/search${suffix}`)
      .then((r) => r.data);
  },
  createConductor: (input: {
    name: string;
    type: ConductorType;
    endpoint?: string | null;
    note?: string | null;
    enabled?: boolean;
  }) => apiClient.post<Conductor>('/api/admin/conductors', input).then((r) => r.data),
  updateConductor: (
    id: number,
    input: {
      name?: string;
      type?: ConductorType;
      endpoint?: string | null;
      note?: string | null;
      enabled?: boolean;
    },
  ) => apiClient.patch<Conductor>(`/api/admin/conductors/${id}`, input).then((r) => r.data),
  deleteConductor: (id: number) =>
    apiClient.delete<{ ok: true }>(`/api/admin/conductors/${id}`).then((r) => r.data),

  // マウントポイント（論理名↔DIRマスター）CRUD
  listMountPoints: () =>
    apiClient.get<MountPoint[]>('/api/admin/mount-points').then((r) => r.data),
  searchMountPoints: (params: ListMountPointsParams = {}) => {
    const qs = new URLSearchParams(listQs(params).replace(/^\?/, ''));
    if (params.enabled !== undefined) qs.set('enabled', String(params.enabled));
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiClient
      .get<Paginated<MountPoint>>(`/api/admin/mount-points/search${suffix}`)
      .then((r) => r.data);
  },
  createMountPoint: (input: {
    name: string;
    path: string;
    description?: string | null;
    enabled?: boolean;
  }) => apiClient.post<MountPoint>('/api/admin/mount-points', input).then((r) => r.data),
  updateMountPoint: (
    id: number,
    input: {
      name?: string;
      path?: string;
      description?: string | null;
      enabled?: boolean;
    },
  ) => apiClient.patch<MountPoint>(`/api/admin/mount-points/${id}`, input).then((r) => r.data),
  deleteMountPoint: (id: number) =>
    apiClient.delete<{ ok: true }>(`/api/admin/mount-points/${id}`).then((r) => r.data),

  // 用途マスタ（device-mount の「用途」選択肢）CRUD。ドロップダウン用に全件返しは温存。
  listMountPurposes: () =>
    apiClient.get<MountPurpose[]>('/api/admin/mount-purposes').then((r) => r.data),
  // 一覧画面用：サーバ側 検索(q=あいまい)/有効無効(enabled=ドロップダウン)/ソート/ページング。
  searchMountPurposes: (params: ListMountPurposesParams = {}) => {
    const qs = new URLSearchParams(listQs(params).replace(/^\?/, ''));
    if (params.enabled !== undefined) qs.set('enabled', String(params.enabled));
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiClient
      .get<Paginated<MountPurpose>>(`/api/admin/mount-purposes/search${suffix}`)
      .then((r) => r.data);
  },
  createMountPurpose: (input: { label: string; sort?: number; enabled?: boolean }) =>
    apiClient.post<MountPurpose>('/api/admin/mount-purposes', input).then((r) => r.data),
  updateMountPurpose: (id: number, input: { label?: string; sort?: number; enabled?: boolean }) =>
    apiClient.patch<MountPurpose>(`/api/admin/mount-purposes/${id}`, input).then((r) => r.data),
  deleteMountPurpose: (id: number) =>
    apiClient.delete<{ ok: true }>(`/api/admin/mount-purposes/${id}`).then((r) => r.data),

  // ── マスタ管理（#176）: 区分(type)＋配下の責務(role)。責務は区分に従属 ──
  listAgentMasters: () =>
    apiClient
      .get<{ types: AgentTypeWithRoles[] }>('/api/admin/agent-masters')
      .then((r) => r.data),
  createAgentType: (input: { code: string; label: string; sort?: number; enabled?: boolean }) =>
    apiClient.post<AgentTypeMaster>('/api/admin/agent-types', input).then((r) => r.data),
  updateAgentType: (
    id: number,
    input: { label?: string; sort?: number; enabled?: boolean },
  ) => apiClient.patch<AgentTypeMaster>(`/api/admin/agent-types/${id}`, input).then((r) => r.data),
  deleteAgentType: (id: number) =>
    apiClient.delete<{ ok: true }>(`/api/admin/agent-types/${id}`).then((r) => r.data),
  createAgentRole: (input: {
    typeId: number;
    code: string;
    label: string;
    sort?: number;
    enabled?: boolean;
  }) => apiClient.post<AgentRoleMaster>('/api/admin/agent-roles', input).then((r) => r.data),
  updateAgentRole: (
    id: number,
    input: { typeId?: number; code?: string; label?: string; sort?: number; enabled?: boolean },
  ) => apiClient.patch<AgentRoleMaster>(`/api/admin/agent-roles/${id}`, input).then((r) => r.data),
  deleteAgentRole: (id: number) =>
    apiClient.delete<{ ok: true }>(`/api/admin/agent-roles/${id}`).then((r) => r.data),

  // ── /コマンドマスタ（CommandMaster・AI-WF #91）: 諸元の CRUD ＋ チャット用の有効一覧 ──
  listCommands: (params: ListCommandsParams = {}) => {
    const qs = new URLSearchParams();
    if (params.scope) qs.set('scope', params.scope);
    if (params.kind) qs.set('kind', params.kind);
    if (params.projectId !== undefined) qs.set('projectId', String(params.projectId));
    if (params.teamId !== undefined) qs.set('teamId', String(params.teamId));
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiClient.get<CommandMaster[]>(`/api/admin/commands${suffix}`).then((r) => r.data);
  },
  createCommand: (input: CommandInput) =>
    apiClient.post<CommandMaster>('/api/admin/commands', input).then((r) => r.data),
  updateCommand: (id: number, input: Partial<CommandInput>) =>
    apiClient.patch<CommandMaster>(`/api/admin/commands/${id}`, input).then((r) => r.data),
  deleteCommand: (id: number) =>
    apiClient.delete<{ ok: true }>(`/api/admin/commands/${id}`).then((r) => r.data),
  // チャットの「/ 一覧」を引く口。global＋project＋team を合成（同名は team>project>global）。
  availableCommands: (projectId?: number, teamId?: number) => {
    const qs = new URLSearchParams();
    if (projectId !== undefined) qs.set('projectId', String(projectId));
    if (teamId !== undefined) qs.set('teamId', String(teamId));
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiClient
      .get<CommandMaster[]>(`/api/admin/commands/available${suffix}`)
      .then((r) => r.data);
  },

  listDevices: () =>
    apiClient.get<DeviceListItem[]>('/api/admin/devices').then((r) => r.data),
  // 一覧画面用：サーバ側 検索(q=あいまい)/OS・配置(ドロップダウン)/ソート/ページング（listDevices は温存）。
  searchDevices: (params: ListDevicesParams = {}) => {
    const qs = new URLSearchParams(listQs(params).replace(/^\?/, ''));
    if (params.osType) qs.set('osType', params.osType);
    if (params.placementType) qs.set('placementType', params.placementType);
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiClient
      .get<Paginated<DeviceListItem>>(`/api/admin/devices/search${suffix}`)
      .then((r) => r.data);
  },
  createDevice: (input: {
    name: string;
    nickname?: string | null;
    tunnelHost?: string | null;
    placementType: PlacementType;
    osType: OsType;
    heartbeatEnabled?: boolean;
  }) => apiClient.post<NewDeviceResponse>('/api/admin/devices', input).then((r) => r.data),
  updateDevice: (
    id: number,
    input: {
      name?: string;
      nickname?: string | null;
      // 空文字を渡すと Backend 側で null（未登録）に戻る。
      tunnelHost?: string | null;
      placementType?: PlacementType;
      osType?: OsType;
      heartbeatEnabled?: boolean;
    },
  ) => apiClient.patch<DeviceListItem>(`/api/admin/devices/${id}`, input).then((r) => r.data),
  deleteDevice: (id: number) =>
    apiClient.delete<{ ok: true }>(`/api/admin/devices/${id}`).then((r) => r.data),
  revokeDeviceJwt: (id: number) =>
    apiClient
      .post<{ ok: true; jwtRevoked: boolean }>(`/api/admin/devices/${id}/revoke`)
      .then((r) => r.data),
  unrevokeDeviceJwt: (id: number) =>
    apiClient
      .post<{ ok: true; jwtRevoked: boolean }>(`/api/admin/devices/${id}/unrevoke`)
      .then((r) => r.data),
  // 非失効トークンを発行/再発行して返す（UI 表示・コピー用）。#22。
  issueDeviceToken: (id: number) =>
    apiClient
      .post<{ token: string; deviceId: number }>(`/api/admin/devices/${id}/token`)
      .then((r) => r.data),

  // WEB からの claude ログイン(ルートA): setup-token の長期トークンを配布。
  // この端末だけ / 全端末一括。トークンは保存されず worker の env に入る。
  setClaudeToken: (id: number, token: string) =>
    apiClient
      .post<{ deviceId: number; delivered: boolean; reason?: string }>(
        `/api/admin/devices/${id}/claude-token`,
        { token },
      )
      .then((r) => r.data),
  broadcastClaudeToken: (token: string) =>
    apiClient
      .post<{
        total: number;
        deliveredCount: number;
        results: { deviceId: number; name: string; delivered: boolean }[];
      }>(`/api/admin/devices/claude-token-broadcast`, { token })
      .then((r) => r.data),

  // === Claude マスタ認証 (AI-WF #50) ===
  getClaudeMaster: () =>
    apiClient
      .get<{ hasToken: boolean; fingerprint: string | null }>(
        `/api/admin/claude-master`,
      )
      .then((r) => r.data),
  startClaudeDistribute: (token?: string) =>
    apiClient
      .post<ClaudeDistJob>(
        `/api/admin/claude-master/distribute`,
        token ? { token } : {},
      )
      .then((r) => r.data),
  getClaudeDistribute: (jobId: string) =>
    apiClient
      .get<ClaudeDistJob>(`/api/admin/claude-master/distribute/${jobId}`)
      .then((r) => r.data),
  applyMasterToDevice: (id: number) =>
    apiClient
      .post<ClaudeDistDevice>(
        `/api/admin/devices/${id}/claude-token/apply-master`,
      )
      .then((r) => r.data),

  // パス登録。projectId 省略時は未束ね（デバイス画面はこれを使う。束ねはプロジェクト画面）。
  createDeviceProjectMap: (input: {
    deviceId: number;
    projectId?: number | null;
    mountPointId: number;
    purpose?: string | null;
    cliType?: CliType;
    cliOptions?: Record<string, unknown>;
    isRepresentative?: boolean;
  }) =>
    apiClient
      .post<DeviceProjectMapEntry>('/api/admin/device-project-map', input)
      .then((r) => r.data),
  // パス変更（MountPoint 差し替え / cliType）。projectId への束ねは別口。
  updateDeviceProjectMap: (
    mapId: number,
    input: {
      mountPointId?: number;
      purpose?: string | null;
      cliType?: CliType;
      cliOptions?: Record<string, unknown>;
    },
  ) =>
    apiClient
      .patch<DeviceProjectMapEntry>(`/api/admin/device-project-map/${mapId}`, input)
      .then((r) => r.data),
  deleteDeviceProjectMap: (mapId: number) =>
    apiClient
      .delete<{ ok: true }>(`/api/admin/device-project-map/${mapId}`)
      .then((r) => r.data),
  // デバイスパスの有効/無効を切り替える（削除と違いパスは残る）。
  // enabled=false で無効化（配信・ルーティング対象外）、true で再有効化。
  setDeviceProjectMapEnabled: (mapId: number, enabled: boolean) =>
    apiClient
      .patch<DeviceProjectMapEntry>(`/api/admin/device-project-map/${mapId}/enabled`, {
        enabled,
      })
      .then((r) => r.data),
  // デバイスパスをプロジェクトへ束ねる / 束ねを外す（projectId=null で解除）。
  // パス自体はデバイスに残る。プロジェクト画面のディレクトリ割当で使う。
  assignPathToProject: (mapId: number, projectId: number | null) =>
    apiClient
      .patch<DeviceProjectMapEntry>(`/api/admin/device-project-map/${mapId}/project`, {
        projectId,
      })
      .then((r) => r.data),
  // アサイン本体：DIR に担当チームを割り当て/解除（teamId=null で解除）。1 DIR = 高々1チーム。
  assignPathTeam: (mapId: number, teamId: number | null) =>
    apiClient
      .patch<DeviceProjectMapEntry>(`/api/admin/device-project-map/${mapId}/team`, { teamId })
      .then((r) => r.data),
  // 1DIR複数チーム: primary 以外の「追加チーム」集合を丸ごと置換（空配列で全解除）。
  setPathTeams: (mapId: number, teamIds: number[]) =>
    apiClient
      .put<{ mapId: number; teamIds: number[] }>(
        `/api/admin/device-project-map/${mapId}/teams`,
        { teamIds },
      )
      .then((r) => r.data),
  // hello メッセージを更新（null で空欄に戻す）。
  updateHelloMessage: (mapId: number, helloMessage: string | null) =>
    apiClient
      .patch<DeviceProjectMapEntry>(`/api/admin/device-project-map/${mapId}/hello-message`, {
        helloMessage,
      })
      .then((r) => r.data),

  testRun: (
    deviceId: number,
    input: {
      projectId: number;
      prompt: string;
      cwd: string;
      options?: Record<string, unknown>;
    },
  ) =>
    apiClient
      .post<{ requestId: string; delivered: boolean; reason?: string }>(
        `/api/admin/devices/${deviceId}/test-run`,
        input,
      )
      .then((r) => r.data),

  listRequestLogs: (params: { direction?: RequestDirection; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.direction) qs.set('direction', params.direction);
    if (params.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiClient
      .get<RequestLogRow[]>(`/api/admin/request-logs${suffix}`)
      .then((r) => r.data);
  },

  offlineQueueStatus: () =>
    apiClient
      .get<{
        pendingCount: number;
        deliveredCount: number;
        pausedDeviceIds: number[];
      }>('/api/admin/offline-queue/status')
      .then((r) => r.data),
  listOfflineQueue: (params: { pendingOnly?: boolean; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.pendingOnly) qs.set('pendingOnly', 'true');
    if (params.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiClient
      .get<OfflineQueueRow[]>(`/api/admin/offline-queue${suffix}`)
      .then((r) => r.data);
  },
  deleteOfflineQueueEntry: (id: number) =>
    apiClient.delete<{ count: number }>(`/api/admin/offline-queue/${id}`).then((r) => r.data),
  drainOfflineQueueForDevice: (deviceId: number) =>
    apiClient
      .post<{ delivered: number; total: number }>(
        `/api/admin/offline-queue/devices/${deviceId}/drain`,
      )
      .then((r) => r.data),
  pauseOfflineQueueForDevice: (deviceId: number) =>
    apiClient
      .post<{ ok: true; paused: true }>(
        `/api/admin/offline-queue/devices/${deviceId}/pause`,
      )
      .then((r) => r.data),

  startSlackInstall: () =>
    apiClient.get<{ url: string }>('/api/auth/slack/install').then((r) => r.data),
};

export interface OfflineQueueRow {
  id: number;
  tenantId: number;
  targetDeviceId: number;
  slackChannelId: number;
  slackThreadTs: string | null;
  payload: string;
  receivedAt: string;
  deliveredAt: string | null;
  deletedAt: string | null;
  slackChannel: { id: number; name: string; channelId: string };
  targetDevice: { id: number; name: string };
}

export type RequestDirection =
  | 'slack_in'
  | 'device_out'
  | 'device_in'
  | 'slack_out'
  | 'peer_message'
  | 'vscode_origin';

export interface RequestLogRow {
  id: number;
  tenantId: number;
  direction: RequestDirection;
  requestId: string;
  payload: string;
  deviceId: number | null;
  projectId: number | null;
  slackChannelId: number | null;
  slackThreadTs: string | null;
  meta: Record<string, unknown>;
  createdAt: string;
}
